import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Search,
  Truck,
  Fuel,
  Wrench,
  Calendar,
  MapPin,
  User,
  X,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Gauge,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const vehicleStatuses = [
  { id: 'available', label: 'Available', color: 'bg-green-100 text-green-700' },
  { id: 'in_use', label: 'In Use', color: 'bg-blue-100 text-blue-700' },
  { id: 'maintenance', label: 'Maintenance', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'out_of_service', label: 'Out of Service', color: 'bg-red-100 text-red-700' }
];

const fuelTypes = [
  { id: 'gasoline', label: 'Gasoline' },
  { id: 'diesel', label: 'Diesel' },
  { id: 'electric', label: 'Electric' },
  { id: 'hybrid', label: 'Hybrid' }
];

const maintenanceTypes = [
  { id: 'oil_change', label: 'Oil Change' },
  { id: 'tire_rotation', label: 'Tire Rotation' },
  { id: 'inspection', label: 'Inspection' },
  { id: 'repair', label: 'Repair' },
  { id: 'brakes', label: 'Brakes' },
  { id: 'other', label: 'Other' }
];

export const VehicleFleet = () => {
  const { api } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleHistory, setVehicleHistory] = useState(null);
  const [activeTab, setActiveTab] = useState('fleet');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, summaryRes, employeesRes] = await Promise.all([
        api.get('/vehicles/'),
        api.get('/vehicles/summary'),
        api.get('/employees')
      ]);
      setVehicles(vehiclesRes.data);
      setSummary(summaryRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Failed to load vehicle data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async (data) => {
    try {
      await api.post('/vehicles/', data);
      toast.success('Vehicle added');
      fetchData();
      setShowAddModal(false);
    } catch (error) {
      toast.error('Failed to add vehicle');
    }
  };

  const handleRecordMaintenance = async (data) => {
    try {
      await api.post('/vehicles/maintenance', data);
      toast.success('Maintenance recorded');
      fetchData();
      setShowMaintenanceModal(false);
      setSelectedVehicle(null);
    } catch (error) {
      toast.error('Failed to record maintenance');
    }
  };

  const handleRecordFuel = async (data) => {
    try {
      await api.post('/vehicles/fuel', data);
      toast.success('Fuel log added');
      fetchData();
      setShowFuelModal(false);
      setSelectedVehicle(null);
    } catch (error) {
      toast.error('Failed to record fuel');
    }
  };

  const fetchVehicleHistory = async (vehicleId) => {
    try {
      const response = await api.get(`/vehicles/${vehicleId}/history`);
      setVehicleHistory(response.data);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error('Failed to load vehicle history');
    }
  };

  const filteredVehicles = vehicles.filter(v => {
    const matchesSearch = v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          v.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          v.license_plate.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !filterStatus || v.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const getStatusInfo = (status) => vehicleStatuses.find(s => s.id === status) || vehicleStatuses[0];

  return (
    <div className="space-y-6" data-testid="vehicle-fleet-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Vehicle Fleet</h1>
          <p className="text-gray-500 mt-1">Manage company vehicles and maintenance</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          data-testid="add-vehicle-btn"
        >
          <Plus className="w-4 h-4" />
          Add Vehicle
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-100 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Total Vehicles</span>
            </div>
            <p className="font-heading text-2xl">{summary.total_vehicles}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-gray-100 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Available</span>
            </div>
            <p className="font-heading text-2xl text-green-600">{summary.status_breakdown?.available || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-gray-100 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Yearly Costs</span>
            </div>
            <p className="font-heading text-2xl text-purple-600">${summary.yearly_costs?.total?.toLocaleString() || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-gray-100 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Service Due</span>
            </div>
            <p className="font-heading text-2xl text-orange-600">{summary.service_due_soon?.length || 0}</p>
          </motion.div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200">
        {['fleet', 'service-due'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 px-2 text-sm font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'text-[#C6A87C] border-b-2 border-[#C6A87C]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'service-due' ? 'Service Due' : 'Fleet'}
          </button>
        ))}
      </div>

      {activeTab === 'fleet' && (
        <>
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search vehicles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
            >
              <option value="">All Statuses</option>
              {vehicleStatuses.map((status) => (
                <option key={status.id} value={status.id}>{status.label}</option>
              ))}
            </select>
          </div>

          {/* Vehicles Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            {filteredVehicles.map((vehicle) => {
              const statusInfo = getStatusInfo(vehicle.status);
              return (
                <motion.div
                  key={vehicle.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-gray-100 p-6 hover:border-[#C6A87C]/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gray-100 flex items-center justify-center">
                        <Truck className="w-7 h-7 text-gray-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{vehicle.name}</h3>
                        <p className="text-sm text-gray-500">{vehicle.year} {vehicle.make} {vehicle.model}</p>
                        <p className="text-xs text-gray-400">{vehicle.license_plate}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Gauge className="w-4 h-4" />
                      {vehicle.current_mileage?.toLocaleString()} mi
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Fuel className="w-4 h-4" />
                      {vehicle.fuel_type}
                    </div>
                    {vehicle.assigned_employee_name && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 col-span-2">
                        <User className="w-4 h-4" />
                        {vehicle.assigned_employee_name}
                      </div>
                    )}
                  </div>

                  {vehicle.next_service_due && (
                    <div className="bg-yellow-50 p-3 rounded-lg mb-4 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm text-yellow-700">
                        Service due: {new Date(vehicle.next_service_due).toLocaleDateString()}
                      </span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedVehicle(vehicle);
                        setShowMaintenanceModal(true);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition-colors"
                    >
                      <Wrench className="w-4 h-4" />
                      Service
                    </button>
                    <button
                      onClick={() => {
                        setSelectedVehicle(vehicle);
                        setShowFuelModal(true);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition-colors"
                    >
                      <Fuel className="w-4 h-4" />
                      Fuel
                    </button>
                    <button
                      onClick={() => fetchVehicleHistory(vehicle.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#C6A87C] text-white text-sm hover:bg-[#B09265] transition-colors"
                    >
                      History
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </>
      )}

      {activeTab === 'service-due' && summary?.service_due_soon && (
        <div className="space-y-4">
          {summary.service_due_soon.length > 0 ? (
            summary.service_due_soon.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white border border-gray-100 p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-orange-100 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-gray-500">
                      Due: {new Date(item.next_service_due).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const vehicle = vehicles.find(v => v.id === item.id);
                    if (vehicle) {
                      setSelectedVehicle(vehicle);
                      setShowMaintenanceModal(true);
                    }
                  }}
                  className="px-4 py-2 bg-[#C6A87C] text-white text-sm hover:bg-[#B09265] transition-colors"
                >
                  Record Service
                </button>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-600">All vehicles are up to date on service</p>
            </div>
          )}
        </div>
      )}

      {/* Add Vehicle Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddVehicleModal
            employees={employees}
            onClose={() => setShowAddModal(false)}
            onSubmit={handleAddVehicle}
          />
        )}
      </AnimatePresence>

      {/* Record Maintenance Modal */}
      <AnimatePresence>
        {showMaintenanceModal && selectedVehicle && (
          <MaintenanceModal
            vehicle={selectedVehicle}
            onClose={() => {
              setShowMaintenanceModal(false);
              setSelectedVehicle(null);
            }}
            onSubmit={handleRecordMaintenance}
          />
        )}
      </AnimatePresence>

      {/* Record Fuel Modal */}
      <AnimatePresence>
        {showFuelModal && selectedVehicle && (
          <FuelModal
            vehicle={selectedVehicle}
            onClose={() => {
              setShowFuelModal(false);
              setSelectedVehicle(null);
            }}
            onSubmit={handleRecordFuel}
          />
        )}
      </AnimatePresence>

      {/* Vehicle History Modal */}
      <AnimatePresence>
        {showHistoryModal && vehicleHistory && (
          <HistoryModal
            data={vehicleHistory}
            onClose={() => {
              setShowHistoryModal(false);
              setVehicleHistory(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Add Vehicle Modal
const AddVehicleModal = ({ employees, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    vin: '',
    license_plate: '',
    color: '',
    current_mileage: '',
    fuel_type: 'gasoline',
    assigned_employee_id: '',
    registration_expiry: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      year: parseInt(formData.year),
      current_mileage: parseInt(formData.current_mileage) || 0,
      assigned_employee_id: formData.assigned_employee_id || null
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="font-heading text-xl">Add Vehicle</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Vehicle Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              placeholder="e.g., Work Truck #1"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Year</label>
              <input
                type="number"
                required
                min="1990"
                max="2030"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Make</label>
              <input
                type="text"
                required
                value={formData.make}
                onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="Ford"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Model</label>
              <input
                type="text"
                required
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="F-150"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">License Plate</label>
              <input
                type="text"
                required
                value={formData.license_plate}
                onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">VIN (Optional)</label>
              <input
                type="text"
                value={formData.vin}
                onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Current Mileage</label>
              <input
                type="number"
                value={formData.current_mileage}
                onChange={(e) => setFormData({ ...formData, current_mileage: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fuel Type</label>
              <select
                value={formData.fuel_type}
                onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              >
                {fuelTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Assign to Employee (Optional)</label>
            <select
              value={formData.assigned_employee_id}
              onChange={(e) => setFormData({ ...formData, assigned_employee_id: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            >
              <option value="">Not assigned</option>
              {employees.filter(e => e.is_active).map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name} - {emp.role}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Color (Optional)</label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="White"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Registration Expiry</label>
              <input
                type="date"
                value={formData.registration_expiry}
                onChange={(e) => setFormData({ ...formData, registration_expiry: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none resize-none"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-[#C6A87C] text-white py-3 font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          >
            Add Vehicle
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Maintenance Modal
const MaintenanceModal = ({ vehicle, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    vehicle_id: vehicle.id,
    maintenance_type: 'oil_change',
    description: '',
    mileage_at_service: vehicle.current_mileage || '',
    cost: '',
    service_provider: '',
    next_service_mileage: '',
    next_service_date: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      mileage_at_service: parseInt(formData.mileage_at_service),
      cost: parseFloat(formData.cost) || 0,
      next_service_mileage: formData.next_service_mileage ? parseInt(formData.next_service_mileage) : null
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="font-heading text-xl">Record Service</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-500">Vehicle</p>
            <p className="font-semibold">{vehicle.name}</p>
            <p className="text-sm text-gray-500">{vehicle.year} {vehicle.make} {vehicle.model}</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Service Type</label>
            <select
              value={formData.maintenance_type}
              onChange={(e) => setFormData({ ...formData, maintenance_type: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            >
              {maintenanceTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <input
              type="text"
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              placeholder="What was done?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Mileage</label>
              <input
                type="number"
                required
                value={formData.mileage_at_service}
                onChange={(e) => setFormData({ ...formData, mileage_at_service: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cost ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Service Provider</label>
            <input
              type="text"
              value={formData.service_provider}
              onChange={(e) => setFormData({ ...formData, service_provider: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              placeholder="Shop name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Next Service Miles</label>
              <input
                type="number"
                value={formData.next_service_mileage}
                onChange={(e) => setFormData({ ...formData, next_service_mileage: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Next Service Date</label>
              <input
                type="date"
                value={formData.next_service_date}
                onChange={(e) => setFormData({ ...formData, next_service_date: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-[#C6A87C] text-white py-3 font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          >
            Record Service
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Fuel Modal
const FuelModal = ({ vehicle, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    vehicle_id: vehicle.id,
    gallons: '',
    cost: '',
    mileage: vehicle.current_mileage || '',
    fuel_station: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      gallons: parseFloat(formData.gallons),
      cost: parseFloat(formData.cost),
      mileage: parseInt(formData.mileage)
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-heading text-xl">Record Fuel</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-500">Vehicle</p>
            <p className="font-semibold">{vehicle.name}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Gallons</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.gallons}
                onChange={(e) => setFormData({ ...formData, gallons: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Total Cost ($)</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Odometer Reading</label>
            <input
              type="number"
              required
              value={formData.mileage}
              onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Fuel Station (Optional)</label>
            <input
              type="text"
              value={formData.fuel_station}
              onChange={(e) => setFormData({ ...formData, fuel_station: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              placeholder="Where did you fill up?"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-[#C6A87C] text-white py-3 font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          >
            Record Fuel
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

// History Modal
const HistoryModal = ({ data, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="font-heading text-xl">{data.vehicle.name} History</h2>
            <p className="text-sm text-gray-500">{data.vehicle.year} {data.vehicle.make} {data.vehicle.model}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-600">Total Maintenance</p>
              <p className="text-2xl font-bold text-purple-700">${data.total_maintenance_cost?.toLocaleString()}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600">Total Fuel</p>
              <p className="text-2xl font-bold text-blue-700">${data.total_fuel_cost?.toLocaleString()}</p>
            </div>
          </div>

          {/* Maintenance History */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-[#C6A87C]" />
              Maintenance History
            </h3>
            {data.maintenance_history?.length > 0 ? (
              <div className="space-y-2">
                {data.maintenance_history.map((m, i) => (
                  <div key={i} className="border border-gray-100 p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium capitalize">{m.maintenance_type.replace('_', ' ')}</span>
                      <span className="text-sm text-gray-500">{new Date(m.service_date).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-gray-600">{m.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>{m.mileage_at_service?.toLocaleString()} mi</span>
                      <span>${m.cost?.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No maintenance records</p>
            )}
          </div>

          {/* Fuel Logs */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Fuel className="w-5 h-5 text-[#C6A87C]" />
              Fuel Logs
            </h3>
            {data.fuel_logs?.length > 0 ? (
              <div className="space-y-2">
                {data.fuel_logs.slice(0, 10).map((f, i) => (
                  <div key={i} className="border border-gray-100 p-3 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium">{f.gallons} gal @ ${f.cost_per_gallon}/gal</p>
                      <p className="text-xs text-gray-500">{f.mileage?.toLocaleString()} mi</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${f.cost?.toLocaleString()}</p>
                      {f.mpg_since_last && (
                        <p className="text-xs text-green-600">{f.mpg_since_last} MPG</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No fuel logs</p>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VehicleFleet;
